/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { defineMessages } from 'react-intl';
import { BaseEventHandlerDependencies, BaseEventParams } from 'src/core/types/eventBusEvent';
import { ToastStore } from 'src/luis/core/stores/ToastStore';
import { ToastType } from 'src/luis/projects/events/types/Toast';
import { ToastStoreHouse } from 'src/luis/types/stores/events/ToastStoreHouse';

/**
 * Define the toaster messages here
 */
const messages = defineMessages({
    runningMessage: { id: 'luis.events.runningMessage', defaultMessage: 'The operation is running' },
    completedMessage: { id: 'luis.events.completedMessage', defaultMessage: 'The operation completed' },
    failedMessage: { id: 'luis.events.failedMessage', defaultMessage: 'The operation failed' }
});

/**
 * This function decorates a default event handler with the eventName
 * used to cover the common events that don't need custom handler.
 * You have to register it yourself in App.tsx to handle certain events.
 *
 * To use the handler you've to register it in the useEventsInstaller hook.
 *
 * e.g.: defaultEventsHandler('createIntent'), defaultEventsHandler('deleteIntent')
 * @param dependencies Required dependencies passed from the events installer.
 */
export const genericEventHandler = (eventName: string) => (dependencies: BaseEventHandlerDependencies) => {
    const { rootStore, eventBus, localize } = dependencies;

    eventBus.on<BaseEventParams>({
        kind: eventName,
        callback: params => {
            // This switch case can be extracted in a helper function since it'll be used a lot.
            switch (params.eventType) {
                case 'running':
                    findOrCreateToast(rootStore.toastStoreHouse, params.id, {
                        title: localize(messages.runningMessage),
                        status: 'running'
                    });
                    break;

                case 'completed':
                    findOrCreateToast(rootStore.toastStoreHouse, params.id, {
                        title: localize(messages.completedMessage),
                        status: 'completed'
                    });
                    break;

                case 'failed':
                    findOrCreateToast(rootStore.toastStoreHouse, params.id, { title: localize(messages.failedMessage), status: 'failed' });
                    break;

                default:
                    throw new Error('Invalid event type');
            }
        }
    });
};

/**
 * Helper function to create new toast for the givin id and givin data if not exists
 */
const findOrCreateToast = (storeHouse: ToastStoreHouse, id: string, toastData: Pick<ToastType, 'title' | 'status'>) => {
    const store = storeHouse.findById(id);

    if (store) {
        store.updateToast(toastData);
    } else {
        storeHouse.add(new ToastStore(id, toastData));
    }
};
